import React, { memo, useCallback, useEffect, useMemo } from "react";
import { NodeProps, useReactFlow, useUpdateNodeInternals } from "reactflow";
import { NodeType, SourceHandle } from "../../../models/nodeType";
import { FieldErrors, useFieldArray, useForm, UseFormRegister } from "react-hook-form";
import {
  Button,
  Center,
  Flex,
  FormControl,
  Icon,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { ulid } from "ulid";
import { MdClose, MdDragHandle } from "react-icons/md";
import { useDnD } from "../../../hooks/useDnD";
import { useUpdateNodeHandles } from "../../../hooks/useUpdateNodeHandles";
import { useUpdateNodeData } from "../../../hooks/useUpdateNodeData";
import { InputError } from "../../base/InputError";
import SelectAllegiance from "../../base/SelectAllegiance";
import { FlowNodeWithChildren } from "./FlowNode";

export interface RelationshipBand {
  bandId: string;
  label: string;
  min: number;
}

interface FormData {
  bands: RelationshipBand[];
}

const getBandHandleLabel = ({ label, min }: RelationshipBand): string => {
  return label?.trim() ? label.trim() : `≥ ${min}`;
};

interface BandComponentProps {
  index: number;
  updateIndex: (oldIndex: number, newIndex: number) => void;
  removeIndex: (index: number) => void;
  register: UseFormRegister<FormData>;
  errors: FieldErrors<FormData>;
  validateMin: (index: number, value: number) => string | true;
  color?: string;
}

const BandComponent: React.FC<BandComponentProps> = ({
  index,
  updateIndex,
  removeIndex,
  register,
  errors,
  validateMin,
  color,
}) => {
  const { handlerId, isDragging, ref, refPreview } = useDnD("bands", index, updateIndex);

  let fieldErrors = undefined;

  if (errors?.bands?.at) {
    fieldErrors = errors.bands.at(index);
  }

  return (
    <Flex borderColor={color} borderRadius={0} borderWidth={1} ref={refPreview} opacity={isDragging ? 0.25 : 1}>
      <Center p={1} pl={3} ref={ref} data-handler-id={handlerId} cursor={"move"}>
        <Icon as={MdDragHandle} />
      </Center>
      <Stack flexGrow={1}>
        <Flex p={2} ml={2} bg={color} alignItems={"center"} justifyContent={"space-between"}>
          <Text color={"white"}>Band {index}</Text>
          <IconButton
            size={"xs"}
            color={"white"}
            variant={"ghost"}
            icon={<Icon as={MdClose} />}
            aria-label={"delete band"}
            onClick={() => removeIndex(index)}
          />
        </Flex>
        <Flex p={2} gap={2}>
          <FormControl>
            <Input
              id={`bands.${index}.label`}
              placeholder={"band label (optional)"}
              {...register(`bands.${index}.label`)}
            />
          </FormControl>
          <FormControl>
            <Input
              id={`bands.${index}.min`}
              type={"number"}
              step={"any"}
              placeholder={"minimum allegiance"}
              {...register(`bands.${index}.min`, {
                valueAsNumber: true,
                validate: (value) => validateMin(index, value),
              })}
            />
            {fieldErrors?.min && <InputError errorMessage={fieldErrors?.min.message} />}
          </FormControl>
        </Flex>
      </Stack>
    </Flex>
  );
};

interface RelationshipCheckNodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  bands: RelationshipBand[];
  onUpdateBands: (bands: RelationshipBand[]) => void;
  color?: string;
}

export const RelationshipCheckNodeModal: React.FC<RelationshipCheckNodeModalProps> = ({
  isOpen,
  onClose,
  bands,
  onUpdateBands,
  color,
}) => {
  const {
    reset,
    register,
    control,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: useMemo(
      () => ({
        bands,
      }),
      [bands]
    ),
    mode: "onBlur",
  });

  const validateMin = useCallback(
    (index: number, value: number): string | true => {
      if (!isFinite(value)) {
        return "This is required";
      }
      if (value < -1 || value > 1) {
        return "Allegiance is between -1 and 1";
      }
      const bands = getValues("bands") ?? [];
      const isDuplicate = bands.some((band, bandIndex) => bandIndex !== index && Number(band.min) === value);
      if (isDuplicate) {
        return "Each band must have a unique minimum";
      }
      return true;
    },
    [getValues]
  );

  useEffect(() => {
    reset({
      bands,
    });
  }, [bands]);

  const {
    fields: bandFields,
    insert: insertBand,
    remove: removeBand,
    move: moveBand,
  } = useFieldArray({
    name: "bands",
    control,
  });

  const handleUpdate = useCallback(
    ({ bands }: FormData) => {
      onUpdateBands(bands);

      onClose();
    },
    [onClose, onUpdateBands]
  );

  const handleInsertBand = useCallback(
    (index: number) => {
      insertBand(index, {
        bandId: ulid(),
        label: "",
        min: 0,
      });
    },
    [insertBand]
  );

  const handleRemoveBand = useCallback(
    (index: number) => {
      removeBand(index);
    },
    [removeBand]
  );

  const handleMoveBand = useCallback(
    (oldIndex: number, newIndex: number) => {
      moveBand(oldIndex, isFinite(Number(newIndex)) ? Number(newIndex) : oldIndex);
    },
    [moveBand]
  );

  const handleCancel = useCallback(() => {
    reset({
      bands,
    });

    onClose();
  }, [onClose, reset, bands]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={"2xl"}>
      <ModalOverlay />
      <ModalContent bg={"theme.dark.background"} borderColor={color} borderRadius={0} borderWidth={1}>
        <form onSubmit={handleSubmit(handleUpdate)}>
          <ModalHeader>
            <Text color={color}>Configuration</Text>
          </ModalHeader>

          <ModalBody>
            <Stack>
              <Text casing={"uppercase"} color={color}>
                Allegiance Bands
              </Text>
              <Text color={"white"} fontSize={"sm"}>
                Allegiance is between -1 and 1. Each band only has a minimum, so bands can never overlap or leave gaps:
                a band runs from its own minimum up to the next band&apos;s minimum, and the band with the highest
                minimum at or below the player&apos;s allegiance is chosen. Every band output must be connected.
              </Text>
              <Stack>
                {bandFields && bandFields.length === 0 ? (
                  <Center>
                    <Text color={"white"}>No bands (insert new)</Text>
                  </Center>
                ) : (
                  bandFields.map(({ id }, index) => (
                    <BandComponent
                      key={id}
                      index={index}
                      updateIndex={handleMoveBand}
                      removeIndex={handleRemoveBand}
                      register={register}
                      errors={errors}
                      validateMin={validateMin}
                      color={color}
                    />
                  ))
                )}
              </Stack>

              <Flex justifyContent={"flex-end"}>
                <Button onClick={() => handleInsertBand(bandFields.length)} variant={"outline"}>
                  <Text color={color} textTransform={"uppercase"}>
                    Insert New
                  </Text>
                </Button>
              </Flex>
            </Stack>
          </ModalBody>
          <ModalFooter gap={1}>
            <Button onClick={handleCancel} color={"white"} variant={"outline"}>
              Cancel
            </Button>
            <Button color={color} type={"submit"} variant={"outline"}>
              Update
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};

interface RelationshipCheckNodeData {
  allegianceId: string;
  bands: RelationshipBand[];
}

const RelationshipCheckNode: React.FC<NodeProps<NodeType>> = (props) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    id: nodeId,
    data: { color, nodeData, sourceHandles },
  } = props;

  const formData = nodeData as RelationshipCheckNodeData | undefined;
  const allegianceId = formData?.allegianceId ?? "";
  const bands = formData?.bands ?? [];

  const reactFlow = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const { updateNodeSourceHandles } = useUpdateNodeHandles(nodeId);
  const { updateNodeData } = useUpdateNodeData<RelationshipCheckNodeData>(nodeId);

  const handleUpdateBands = useCallback(
    (bands: RelationshipBand[]) => {
      reactFlow.setNodes((nodes) => {
        const node = nodes.find(({ id }) => id === nodeId);

        if (node == null) {
          return nodes;
        }

        const nodeDataCloned = structuredClone(node.data) as NodeType;

        const nodeData = (nodeDataCloned.nodeData as RelationshipCheckNodeData) ?? {};
        nodeData.bands = bands;

        const nodeSources = nodeDataCloned.sourceHandles ?? [];

        const sources: SourceHandle[] = bands.map((band, index) => {
          const source = nodeSources.find(({ linkedNodeDataId }) => linkedNodeDataId === band.bandId);

          if (source != null) {
            return source;
          }

          return {
            label: getBandHandleLabel(band),
            handleName: index.toString(),
            handleType: "source",
            handleCategory: "flow",
            linkedNodeDataId: band.bandId,
          };
        });

        sources.forEach((source, index) => {
          source.label = getBandHandleLabel(bands[index]);
          source.handleName = index.toString();

          if (nodeSources.at(index)?.handleName === "out") {
            source.handleId = nodeSources.at(index)?.handleId;
          }
        });

        if (sources.length === 0) {
          const [nodeSource] = nodeSources;

          sources.push({
            ...nodeSource,
            label: "OUT",
            handleName: "out",
            linkedNodeDataId: undefined,
          });
        }

        node.data = {
          ...nodeDataCloned,
          nodeData,
        };

        updateNodeSourceHandles(sources);

        return nodes;
      });

      updateNodeInternals(nodeId);
    },
    [reactFlow, nodeId, updateNodeInternals, updateNodeSourceHandles]
  );

  useEffect(() => {
    if (sourceHandles?.length) {
      return;
    }

    updateNodeSourceHandles([
      {
        label: "OUT",
        handleName: "out",
        handleType: "source",
        handleCategory: "flow",
      },
    ]);
  }, [sourceHandles]);

  return (
    <>
      <FlowNodeWithChildren {...props}>
        <Stack>
          <SelectAllegiance
            color={color}
            value={allegianceId}
            setValue={(value) => updateNodeData({ allegianceId: value })}
          />
          <Button w={"100%"} onClick={onOpen} variant={"outline"}>
            <Text color={color} textTransform={"uppercase"}>
              Configure Bands
            </Text>
          </Button>
        </Stack>
      </FlowNodeWithChildren>

      <RelationshipCheckNodeModal
        isOpen={isOpen}
        onClose={onClose}
        bands={bands}
        onUpdateBands={handleUpdateBands}
        color={color}
      />
    </>
  );
};

export default memo(RelationshipCheckNode);
